import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { api } from '../utils/api';
import { isModernKpiStore } from '../utils/kpiModernAccess';
import { getWorkingDays, getWorkingRows } from '../utils/workingDays';
import KpiModernTestPage from './KpiModernTestPage';

const MOIS = ['','Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MOIS_COURT = ['','Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

// ========== SVG BAR CHART (stats) ==========
function SvgBarChart({ data, title, valueKey, objKey, unit, colorBar, colorCurrentMonth, decimals = 0 }) {
  const [tooltip, setTooltip] = useState(null);
  if (!data || data.length === 0) return null;
  const allValues = data.flatMap(d => [d[valueKey] || 0, d[objKey] || 0]);
  const rawMax = Math.max(...allValues.filter(v => v > 0));
  if (rawMax === 0) return null;
  const step = rawMax > 30000 ? 10000 : rawMax > 15000 ? 5000 : rawMax > 5000 ? 2500 : rawMax > 100 ? (rawMax > 50 ? 10 : 5) : (rawMax > 10 ? 5 : 1);
  const maxVal = Math.ceil(rawMax / step) * step || step;
  const ySteps = Array.from({ length: Math.round(maxVal / step) + 1 }, (_, i) => i * step);
  const chartH = 280, mL = 55, mT = 15, mB = 45, gap = 6;
  const barW = data.length <= 6 ? 50 : data.length <= 12 ? 36 : Math.max(14, Math.min(32, 500 / data.length));
  const chartW = mL + data.length * (barW + gap) + 20;
  const svgH = mT + chartH + mB;
  const yFor = (v) => mT + chartH - (v / maxVal) * chartH;
  const fmt = (v) => decimals === 0 ? v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : v.toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return (
    <div className="kpi-chart-container" style={{ flex: 1, minWidth: 0 }}>
      <h3 className="kpi-chart-title">{title}</h3>
      <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.68rem', color: 'var(--text-muted)' }}><div style={{ width: 10, height: 10, background: colorBar, borderRadius: 2, opacity: 0.75 }} /> Réalisé</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.68rem', color: 'var(--text-muted)' }}><div style={{ width: 10, height: 10, background: colorCurrentMonth, borderRadius: 2, opacity: 0.85 }} /> Mois en cours</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.68rem', color: 'var(--text-muted)' }}><div style={{ width: 10, height: 3, background: '#ef4444', borderRadius: 1 }} /> Objectif</div>
      </div>
      <div style={{ overflowX: 'auto', paddingBottom: 8, position: 'relative' }}>
        <svg width={chartW} height={svgH} style={{ display: 'block' }}>
          {ySteps.map((v, i) => (
            <g key={i}>
              <line x1={mL} y1={yFor(v)} x2={chartW} y2={yFor(v)} stroke="#e5e7eb" strokeWidth={1} />
              <text x={mL - 6} y={yFor(v) + 4} textAnchor="end" fontSize="10" fill="#9ca3af" fontFamily="monospace">
                {v >= 1000 ? `${Math.round(v / 1000)}k${unit}` : `${v}${unit}`}
              </text>
            </g>
          ))}
          <line x1={mL} y1={yFor(0)} x2={chartW} y2={yFor(0)} stroke="#d1d5db" strokeWidth={2} />
          {data.map((d, i) => {
            const x = mL + i * (barW + gap) + gap;
            const val = d[valueKey] || 0, obj = d[objKey] || 0;
            const bY = yFor(val), bH = (val / maxVal) * chartH;
            const isCurMonth = d.year === new Date().getFullYear() && d.month === new Date().getMonth() + 1;
            const at = obj > 0 && val >= obj;
            return (
              <g key={i}>
                <rect x={x} y={bY} width={barW} height={bH} rx={3} fill={isCurMonth ? colorCurrentMonth : colorBar} opacity={isCurMonth ? 0.9 : 0.75} />
                {obj > 0 && <line x1={x - 3} y1={yFor(obj)} x2={x + barW + 3} y2={yFor(obj)} stroke="#ef4444" strokeWidth={3} strokeLinecap="round" />}
                <text x={x + barW / 2} y={yFor(0) + 16} textAnchor="middle" fontSize="10" fill="#6b7280">{MOIS_COURT[d.month]}</text>
                {(i === 0 || d.month === 1) && <text x={x + barW / 2} y={yFor(0) + 28} textAnchor="middle" fontSize="9" fill="#9ca3af" fontWeight="600">{d.year}</text>}
                <rect x={x - 2} y={mT} width={barW + 4} height={chartH + mB} fill="transparent"
                  onMouseEnter={e => { const r = e.currentTarget.closest('svg').parentElement.getBoundingClientRect(); setTooltip({ x: e.clientX - r.left, y: Math.max(80, bY + 10), month: MOIS[d.month], year: d.year, val, obj, at, jours: d.nb_jours_ouvres }); }}
                  onMouseLeave={() => setTooltip(null)} />
              </g>
            );
          })}
        </svg>
        {tooltip && (
          <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, 0)', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', lineHeight: 1.5, pointerEvents: 'none' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{tooltip.month} {tooltip.year}</div>
            <div>Réalisé : <strong>{fmt(tooltip.val)} {unit}</strong></div>
            {tooltip.jours > 0 && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{tooltip.jours}j ouvrés</div>}
            {tooltip.obj > 0 && (
              <>
                <div style={{ borderTop: '1px solid var(--border-light)', marginTop: 6, paddingTop: 6 }}>Objectif : {fmt(tooltip.obj)} {unit}</div>
                <div style={{ fontWeight: 600, color: tooltip.at ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  {tooltip.at ? '✅ Atteint' : `❌ ${fmt(tooltip.val - tooltip.obj)} ${unit}`}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ========== COMP CARD ==========
function CompCard({ title, subtitle, nbJours, current, ref_data, joursRestants, isN1 }) {
  const fN = (v) => v ? v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : '0';
  const fD = (v) => v ? v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';
  const dc = (c, r) => (!r || r === 0) ? 'var(--text-muted)' : c >= r ? 'var(--accent-green)' : 'var(--accent-red)';
  const ds = (c, r) => c >= r ? '+' : '';
  const dp = (c, r) => { if (!r || r === 0) return ''; const p = ((c - r) / r) * 100; return ` (${p >= 0 ? '+' : ''}${p.toFixed(1)}%)`; };
  const caR = isN1 && ref_data.ca > 0 && current.ca > 0 && joursRestants > 0 ? (ref_data.ca - current.ca) / joursRestants : 0;

  return (
    <div className="kpi-comp-card">
      <div className="kpi-comp-card-title">{title}</div>
      <div className="kpi-comp-card-sub">{subtitle} · {nbJours || 0}j données</div>
      <table className="kpi-comp-table">
        <thead><tr><th></th><th>Actuel</th><th>Réf.</th><th>Écart</th></tr></thead>
        <tbody>
          {current.ca > 0 && <tr><td className="kpi-comp-label">CA HT</td><td><strong>{fN(current.ca)} €</strong></td><td>{fN(ref_data.ca)} €</td><td style={{ color: dc(current.ca, ref_data.ca), fontWeight: 600 }}>{ref_data.ca > 0 ? `${ds(current.ca, ref_data.ca)}${fN(current.ca - ref_data.ca)} €${dp(current.ca, ref_data.ca)}` : '—'}</td></tr>}
          {current.pm > 0 && <tr><td className="kpi-comp-label">PM HT</td><td><strong>{fD(current.pm)} €</strong></td><td>{fD(ref_data.pm)} €</td><td style={{ color: dc(current.pm, ref_data.pm), fontWeight: 600 }}>{ref_data.pm > 0 ? `${ds(current.pm, ref_data.pm)}${fD(current.pm - ref_data.pm)} €${dp(current.pm, ref_data.pm)}` : '—'}</td></tr>}
          {current.tickets > 0 && <tr><td className="kpi-comp-label">Tickets</td><td><strong>{fN(current.tickets)}</strong></td><td>{fN(ref_data.tickets)}</td><td style={{ color: dc(current.tickets, ref_data.tickets), fontWeight: 600 }}>{ref_data.tickets > 0 ? `${ds(current.tickets, ref_data.tickets)}${fN(current.tickets - ref_data.tickets)}${dp(current.tickets, ref_data.tickets)}` : '—'}</td></tr>}
        </tbody>
      </table>
      {isN1 && joursRestants > 0 && ref_data.ca > 0 && current.ca > 0 && (
        <div className="kpi-comp-projection">
          <div className="kpi-comp-projection-title">📈 Pour atteindre N-1 ({joursRestants}j restants)</div>
          {caR > 0 ? <div>CA HT/jour : <strong style={{ color: 'var(--accent-blue)' }}>{fN(Math.ceil(caR))} €/j</strong></div> : <div style={{ color: 'var(--accent-green)', fontWeight: 600 }}>✅ CA N-1 déjà dépassé</div>}
        </div>
      )}
    </div>
  );
}

// ========== DAILY CHART (dual axis) ==========
function DailyChart({ dailyData }) {
  const [tooltip, setTooltip] = useState(null);
  if (!dailyData || dailyData.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucune donnée saisie ce mois</div>;

  const chartH = 260, mL = 60, mR = 60, mT = 15, mB = 50, gap = 4;
  const barW = dailyData.length <= 15 ? 28 : dailyData.length <= 25 ? 20 : 14;
  const chartW = mL + mR + dailyData.length * (barW + gap) + 20;
  const svgH = mT + chartH + mB;

  const maxCa = Math.max(...dailyData.map(d => d.ca_ht || 0));
  const cStep = maxCa > 3000 ? 1000 : maxCa > 1500 ? 500 : maxCa > 500 ? 250 : 100;
  const maxCaR = Math.ceil(maxCa / cStep) * cStep || cStep;
  const caSteps = Array.from({ length: Math.round(maxCaR / cStep) + 1 }, (_, i) => i * cStep);

  const maxPm = Math.max(...dailyData.map(d => d.pm_ht || 0));
  const maxTk = Math.max(...dailyData.map(d => d.nb_tickets || 0));
  const maxRight = Math.max(maxPm, maxTk);
  const rStep = maxRight > 100 ? 20 : maxRight > 50 ? 10 : 5;
  const maxRR = Math.ceil(maxRight / rStep) * rStep || rStep;
  const rSteps = Array.from({ length: Math.round(maxRR / rStep) + 1 }, (_, i) => i * rStep);

  const yL = (v) => mT + chartH - (v / maxCaR) * chartH;
  const yR = (v) => mT + chartH - (v / maxRR) * chartH;

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8, position: 'relative' }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.68rem', color: 'var(--text-muted)' }}><div style={{ width: 10, height: 10, background: '#6088d4', borderRadius: 2, opacity: 0.75 }} /> CA HT (€)</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.68rem', color: 'var(--text-muted)' }}><div style={{ width: 10, height: 3, background: '#f59e0b', borderRadius: 1 }} /> PM HT (€)</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.68rem', color: 'var(--text-muted)' }}><div style={{ width: 10, height: 3, background: '#22c55e', borderRadius: 1 }} /> Tickets</div>
      </div>
      <svg width={chartW} height={svgH} style={{ display: 'block' }}>
        {caSteps.map((v, i) => (
          <g key={`l${i}`}>
            <line x1={mL} y1={yL(v)} x2={chartW - mR} y2={yL(v)} stroke="#e5e7eb" strokeWidth={1} />
            <text x={mL - 6} y={yL(v) + 4} textAnchor="end" fontSize="9" fill="#6088d4" fontFamily="monospace">{v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}€</text>
          </g>
        ))}
        {rSteps.map((v, i) => <text key={`r${i}`} x={chartW - mR + 6} y={yR(v) + 4} textAnchor="start" fontSize="9" fill="#9ca3af" fontFamily="monospace">{v}</text>)}
        <line x1={mL} y1={yL(0)} x2={chartW - mR} y2={yL(0)} stroke="#d1d5db" strokeWidth={2} />

        {dailyData.map((d, i) => {
          const x = mL + i * (barW + gap) + gap;
          const caH = maxCaR > 0 ? ((d.ca_ht || 0) / maxCaR) * chartH : 0;
          const day = parseInt(d.date.split('-')[2]);
          return (
            <g key={i}>
              <rect x={x} y={yL(d.ca_ht || 0)} width={barW} height={caH} rx={2} fill="#6088d4" opacity={0.7} />
              <text x={x + barW / 2} y={yL(0) + 14} textAnchor="middle" fontSize="9" fill="#6b7280">{day}</text>
              <rect x={x - 2} y={mT} width={barW + 4} height={chartH + mB} fill="transparent"
                onMouseEnter={e => { const r = e.currentTarget.closest('svg').parentElement.getBoundingClientRect(); setTooltip({ x: e.clientX - r.left, y: Math.max(60, yL(d.ca_ht || 0) + 10), d }); }}
                onMouseLeave={() => setTooltip(null)} />
            </g>
          );
        })}

        <polyline fill="none" stroke="#f59e0b" strokeWidth={2.5} strokeLinejoin="round"
          points={dailyData.map((d, i) => `${mL + i * (barW + gap) + gap + barW / 2},${yR(d.pm_ht || 0)}`).join(' ')} />
        {dailyData.map((d, i) => <circle key={`pm${i}`} cx={mL + i * (barW + gap) + gap + barW / 2} cy={yR(d.pm_ht || 0)} r={3} fill="#f59e0b" />)}

        <polyline fill="none" stroke="#22c55e" strokeWidth={2} strokeLinejoin="round" strokeDasharray="6,3"
          points={dailyData.map((d, i) => `${mL + i * (barW + gap) + gap + barW / 2},${yR(d.nb_tickets || 0)}`).join(' ')} />
        {dailyData.map((d, i) => <circle key={`tk${i}`} cx={mL + i * (barW + gap) + gap + barW / 2} cy={yR(d.nb_tickets || 0)} r={2.5} fill="#22c55e" />)}
      </svg>
      {tooltip && (
        <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, 0)', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', whiteSpace: 'nowrap', zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', lineHeight: 1.6, pointerEvents: 'none' }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{tooltip.d.date}</div>
          <div>CA HT : <strong>{(tooltip.d.ca_ht || 0).toLocaleString('fr-FR')} €</strong></div>
          <div>PM HT : <strong>{(tooltip.d.pm_ht || 0).toFixed(2)} €</strong></div>
          <div>Tickets : <strong>{tooltip.d.nb_tickets || 0}</strong></div>
        </div>
      )}
    </div>
  );
}

// ========== MAIN PAGE ==========
export default function KpiPage({ onStoreChange }) {
  const { isAdmin, canViewStore, canManageStore } = useAuth();
  const toast = useToast();
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const canEnterDailyResults = Boolean(currentStore && canViewStore('chiffres', currentStore.id));
  const canManageKpi = Boolean(currentStore && canManageStore('manage_kpi', currentStore.id));
  const canManageObjectives = Boolean(currentStore && canManageStore('manage_objectives', currentStore.id));
  const [monthlyData, setMonthlyData] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [imports, setImports] = useState([]);
  const [dailyInputs, setDailyInputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showImports, setShowImports] = useState(false);
  const [showObjectives, setShowObjectives] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [editingObj, setEditingObj] = useState(null);
  const [editCa, setEditCa] = useState('');
  const [editPm, setEditPm] = useState('');
  const [kpiView, setKpiView] = useState('daily');
  const [modernExperience, setModernExperience] = useState(true);

  const nowDate = new Date();
  const [diDate, setDiDate] = useState(nowDate.toISOString().split('T')[0]);
  const [diCa, setDiCa] = useState('');
  const [diPm, setDiPm] = useState('');
  const [diTickets, setDiTickets] = useState('');

  const [compStart, setCompStart] = useState(`${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, '0')}-01`);
  const [compEnd, setCompEnd] = useState(nowDate.toISOString().split('T')[0]);
  const [compCa, setCompCa] = useState('');
  const [compPm, setCompPm] = useState('');
  const [compTickets, setCompTickets] = useState('');
  const [compResult, setCompResult] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [situation, setSituation] = useState(null);

  const menuRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { loadStores(); }, []);
  useEffect(() => { if (currentStore) loadData(); }, [currentStore]);
  useEffect(() => { onStoreChange?.(currentStore); }, [currentStore, onStoreChange]);
  useEffect(() => { const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);

  const loadStores = async () => {
    try {
      const data = (await api.getStores()).filter(store => canViewStore('chiffres', store.id));
      setStores(data);
      if (data.length > 0) {
        const sid = parseInt(localStorage.getItem('currentStoreId'));
        setCurrentStore(previous => data.find(s => s.id === previous?.id) || (sid ? data.find(s => s.id === sid) : null) || data[0]);
      } else {
        setCurrentStore(null);
      }
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleStoreChange = (store) => { setCurrentStore(store); setModernExperience(true); localStorage.setItem('currentStoreId', store.id); };

  const loadData = useCallback(async () => {
    if (!currentStore) return;
    setLoading(true);
    try {
      const m = nowDate.getMonth() + 1, y = nowDate.getFullYear();
      const [monthly, imp, objs, di] = await Promise.all([
        api.kpiMonthly(currentStore.id, 48),
        api.kpiImports(currentStore.id),
        api.getAllObjectives(currentStore.id),
        api.kpiDailyInput(currentStore.id, m, y),
      ]);
      setMonthlyData(monthly); setImports(imp); setObjectives(objs); setDailyInputs(di);

      // Auto-compute situation block (N-1, M-1 comparisons from daily_input)
      const monthStr2 = String(m).padStart(2, '0');
      // Always fetch holidays and N-1 full month for projections
      const [hols, n1Full] = await Promise.all([
        api.getHolidays(currentStore.id),
        api.kpiDailyRange(currentStore.id, `${y - 1}-${monthStr2}-01`, `${y - 1}-${monthStr2}-${new Date(y - 1, m, 0).getDate()}`),
      ]);

      // Calculate jours ouvrés for current month
      const holDates = new Set(hols.filter(h => h.date.startsWith(`${y}-${monthStr2}`)).map(h => h.date));
      const openDays = getWorkingDays(y, m, holDates);
      const joursOuvres = openDays.length;
      const countedInputs = getWorkingRows(di, openDays);

      // Get current month objective
      const objCa = objs.find(x => parseInt(x.month) === m && parseInt(x.year) === y && x.line_order === 1);
      const objCaVal = objCa && objCa.value_text ? parseFloat(objCa.value_text) || 0 : 0;

      if (countedInputs.length > 0) {
        const lastDate = countedInputs[countedInputs.length - 1].date;
        const lastD = new Date(lastDate);
        const totalCa = countedInputs.reduce((s, d) => s + (d.ca_ht || 0), 0);
        const totalTickets = countedInputs.reduce((s, d) => s + (d.nb_tickets || 0), 0);
        const avgPm = countedInputs.reduce((s, d) => s + (d.pm_ht || 0), 0) / countedInputs.length;

        // N-1: from 1st to same day number, last year
        const lastDay = lastDate.split('-')[2];
        const n1Start = `${y - 1}-${monthStr2}-01`;
        const n1End = `${y - 1}-${monthStr2}-${lastDay}`;
        // M-1: from 1st to same day number, last month
        const m1MonthStart = new Date(y, m - 2, 1);
        const m1MonthEnd = new Date(y, m - 2, parseInt(lastDay));
        const m1Start = m1MonthStart.toISOString().split('T')[0];
        const m1End = m1MonthEnd.toISOString().split('T')[0];

        const [n1, m1] = await Promise.all([
          api.kpiDailyRange(currentStore.id, n1Start, n1End),
          api.kpiDailyRange(currentStore.id, m1Start, m1End),
        ]);

        let joursRestants = 0;
        for (const d of openDays) {
          const iso = `${y}-${monthStr2}-${String(d).padStart(2, '0')}`;
          if (iso > lastDate) joursRestants++;
        }

        setSituation({
          lastDate, lastDay: lastD, empty: false,
          current: { ca: totalCa, pm: avgPm, tickets: totalTickets, days: countedInputs.length },
          n1: { ...n1, period: `${n1Start} → ${n1End}` },
          m1: { ...m1, period: `${m1Start} → ${m1End}` },
          n1Full, joursOuvres, joursRestants, objCa: objCaVal,
          month: m, year: y,
        });
      } else {
        // No data yet this month
        setSituation({
          empty: true, month: m, year: y,
          joursOuvres, joursRestants: joursOuvres, objCa: objCaVal,
          n1Full, current: { ca: 0, pm: 0, tickets: 0, days: 0 },
          n1: { total_ca_ht: 0, avg_pm_ht: 0, total_tickets: 0, nb_jours: 0 },
          m1: { total_ca_ht: 0, avg_pm_ht: 0, total_tickets: 0, nb_jours: 0 },
        });
      }
    } catch (e) { toast(e.message, 'error'); }
    setLoading(false);
  }, [currentStore, toast]);

  const handleImport = async (e) => {
    const f = e.target.files[0]; if (!f) return; setImporting(true);
    try {
      const r = await api.kpiImport(currentStore.id, f);
      toast(r.message, 'success');
      setShowImport(false);
      if (r.reconciliation) {
        setReconciliation({ ...r.reconciliation, month: r.month, year: r.year });
      }
      loadData();
    } catch (err) { toast(err.message, 'error'); }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeleteMonth = async (month, year) => {
    if (!window.confirm(`Supprimer ${MOIS[month]} ${year} ?`)) return;
    try { await api.kpiDeleteMonth(currentStore.id, month, year); toast('Supprimé', 'success'); loadData(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const getObj = (month, year, lo) => {
    const o = objectives.find(x => parseInt(x.month) === parseInt(month) && parseInt(x.year) === parseInt(year) && x.line_order === lo);
    if (!o || !o.value_text || !o.value_text.trim()) return 0;
    const v = parseFloat(o.value_text); return isNaN(v) ? 0 : v;
  };

  // Build chartData: ensure continuity up to current month (fill gaps with 0)
  const chartData = (() => {
    const raw = monthlyData.map(d => ({ ...d, obj_ca_ht: getObj(d.month, d.year, 1), obj_pm_ht: getObj(d.month, d.year, 2) }));
    if (raw.length === 0) return raw;
    
    // Find first month in data and ensure we go up to current month
    const nowM = nowDate.getMonth() + 1, nowY = nowDate.getFullYear();
    const firstEntry = raw[0];
    const result = [];
    
    let m = firstEntry.month, y = firstEntry.year;
    while (y < nowY || (y === nowY && m <= nowM)) {
      const existing = raw.find(d => d.month === m && d.year === y);
      if (existing) {
        result.push(existing);
      } else {
        // Empty month — fill with zeros + objectives
        result.push({
          month: m, year: y, total_ca_ht: 0, total_ca_ttc: 0, avg_ca_ht: 0,
          avg_indice_vente: 0, avg_pm_ht: 0, total_clients: 0, total_tickets: 0,
          avg_pct_mdd: 0, avg_marge_mdd_total: 0, avg_pct_exclu: 0, avg_coef_marge: 0,
          nb_jours_ouvres: 0,
          obj_ca_ht: getObj(m, y, 1), obj_pm_ht: getObj(m, y, 2),
        });
      }
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return result;
  })();

  // Daily input save
  const handleSaveDailyInput = async () => {
    if (!diCa && !diPm && !diTickets) { toast('Remplissez au moins un champ', 'error'); return; }
    // Check if data exists for this date
    const existing = dailyInputs.find(d => d.date === diDate);
    if (existing) {
      const dateFormatted = new Date(diDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      if (!window.confirm(`⚠️ Attention, les résultats du ${dateFormatted} ont déjà été saisis.\n\nVoulez-vous continuer ?`)) return;
    }
    try {
      await api.kpiDailyInputSave({ store_id: currentStore.id, date: diDate, ca_ht: parseFloat(diCa) || 0, pm_ht: parseFloat(diPm) || 0, nb_tickets: parseInt(diTickets) || 0 });
      toast('Jour sauvegardé', 'success');
      setDiCa(''); setDiPm(''); setDiTickets('');
      loadData();
    } catch (e) { toast(e.message, 'error'); }
  };

  // Load existing daily input when date changes
  useEffect(() => {
    if (!currentStore || !diDate) return;
    const existing = dailyInputs.find(d => d.date === diDate);
    if (existing) {
      setDiCa(existing.ca_ht ? String(existing.ca_ht) : '');
      setDiPm(existing.pm_ht ? String(existing.pm_ht) : '');
      setDiTickets(existing.nb_tickets ? String(existing.nb_tickets) : '');
    } else {
      setDiCa(''); setDiPm(''); setDiTickets('');
    }
  }, [diDate, dailyInputs]);

  // Monthly totals from daily inputs
  const monthTotalCa = dailyInputs.reduce((s, d) => s + (d.ca_ht || 0), 0);
  const monthAvgPm = dailyInputs.length > 0 ? dailyInputs.reduce((s, d) => s + (d.pm_ht || 0), 0) / dailyInputs.length : 0;
  const monthTotalTickets = dailyInputs.reduce((s, d) => s + (d.nb_tickets || 0), 0);
  const monthAvgTickets = dailyInputs.length > 0 ? monthTotalTickets / dailyInputs.length : 0;

  // Switch to comparateur and pre-fill
  const switchToComparateur = () => {
    setKpiView('comparateur');
    setCompResult(null);
    if (dailyInputs.length > 0) {
      setCompCa(String(Math.round(monthTotalCa)));
      setCompPm(String(monthAvgPm.toFixed(2)));
      setCompTickets(String(monthTotalTickets));
    }
  };

  // Comparator
  const handleCompare = async () => {
    if (!compCa && !compPm && !compTickets) { toast('Remplissez au moins un champ', 'error'); return; }
    setCompLoading(true);
    try {
      const sd = new Date(compStart), ed = new Date(compEnd);

      // Fetch holidays for this store and date range
      const hols = await api.getHolidays(currentStore.id, compStart, compEnd);
      const holDates = new Set(hols.map(h => h.date));

      let jo = 0;
      const dc = new Date(sd);
      while (dc <= ed) {
        const iso = dc.toISOString().split('T')[0];
        if (dc.getDay() !== 0 && !holDates.has(iso)) jo++;
        dc.setDate(dc.getDate() + 1);
      }
      const ldom = new Date(ed.getFullYear(), ed.getMonth() + 1, 0);
      // Fetch holidays for rest of month too
      const endOfMonth = ldom.toISOString().split('T')[0];
      const holsRest = await api.getHolidays(currentStore.id, compEnd, endOfMonth);
      const holDatesRest = new Set(holsRest.map(h => h.date));

      let jr = 0;
      const dr = new Date(ed); dr.setDate(dr.getDate() + 1);
      while (dr <= ldom) {
        const iso = dr.toISOString().split('T')[0];
        if (dr.getDay() !== 0 && !holDatesRest.has(iso)) jr++;
        dr.setDate(dr.getDate() + 1);
      }

      const n1s = `${sd.getFullYear() - 1}-${String(sd.getMonth() + 1).padStart(2, '0')}-${String(sd.getDate()).padStart(2, '0')}`;
      const n1e = `${ed.getFullYear() - 1}-${String(ed.getMonth() + 1).padStart(2, '0')}-${String(ed.getDate()).padStart(2, '0')}`;
      const m1sd = new Date(sd.getFullYear(), sd.getMonth() - 1, sd.getDate());
      const m1ed = new Date(ed.getFullYear(), ed.getMonth() - 1, ed.getDate());
      const m1s = m1sd.toISOString().split('T')[0];
      const m1e = m1ed.toISOString().split('T')[0];

      const [n1, m1] = await Promise.all([
        api.kpiDailyRange(currentStore.id, n1s, n1e),
        api.kpiDailyRange(currentStore.id, m1s, m1e),
      ]);

      setCompResult({
        joursOuvres: jo, joursRestants: jr,
        startLabel: compStart, endLabel: compEnd,
        current: { ca: parseFloat(compCa) || 0, pm: parseFloat(compPm) || 0, tickets: parseInt(compTickets) || 0 },
        n1: { ...n1, period: `${n1s} → ${n1e}` },
        m1: { ...m1, period: `${m1s} → ${m1e}` },
      });
    } catch (e) { toast(e.message, 'error'); }
    setCompLoading(false);
  };

  // Objectives
  const startEdit = (m) => { setEditingObj(`${m.year}-${m.month}`); setEditCa(m.caVal); setEditPm(m.pmVal); };
  const saveObjEdit = async (m) => {
    try {
      const ex = await api.getObjectives(currentStore.id, m.month, m.year);
      await api.saveObjectives({ store_id: currentStore.id, month: m.month, year: m.year, lines: [
        { line_order: 1, objective_text: 'CA HT', value_text: editCa },
        { line_order: 2, objective_text: 'PM HT', value_text: editPm },
        ...ex.filter(o => o.line_order >= 3)
      ] });
      toast('Sauvegardé', 'success'); setEditingObj(null); loadData();
    } catch (e) { toast(e.message, 'error'); }
  };

  const getObjTableData = () => Array.from({ length: 19 }, (_, i) => {
    const d = new Date(nowDate.getFullYear(), nowDate.getMonth() + i - 12, 1);
    const m = d.getMonth() + 1, y = d.getFullYear();
    return {
      month: m, year: y,
      caVal: objectives.find(o => parseInt(o.month) === m && parseInt(o.year) === y && o.line_order === 1)?.value_text || '',
      pmVal: objectives.find(o => parseInt(o.month) === m && parseInt(o.year) === y && o.line_order === 2)?.value_text || '',
      kpi: monthlyData.find(k => k.month === m && k.year === y),
      isCurrentMonth: m === (nowDate.getMonth() + 1) && y === nowDate.getFullYear()
    };
  });

  // ========== OBJECTIVES FULL PAGE ==========
  if (showObjectives) {
    return (
      <div className="main-content kpi-modern-objectives-page">
        <div className="store-bar">{stores.map(s => <button key={s.id} className={`store-tab ${currentStore?.id === s.id ? 'active' : ''}`} onClick={() => handleStoreChange(s)}><span className="store-tab-dot" style={{ background: s.color }} />{s.name}</button>)}</div>
        <div className="kpi-topbar"><h2 className="kpi-page-title">🎯 Objectifs mensuels</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowObjectives(false)}>← Retour</button></div>
        <div className="kpi-chart-container"><div style={{ overflowX: 'auto' }}>
          <table className="kpi-obj-table"><thead><tr><th>Mois</th><th>Obj. CA</th><th>CA réalisé</th><th>Écart CA</th><th>Obj. PM</th><th>PM réalisé</th><th>Écart PM</th>{canManageObjectives && <th style={{ width: 70 }}></th>}</tr></thead>
          <tbody>{getObjTableData().map(m => {
            const k = `${m.year}-${m.month}`, ie = editingObj === k;
            const co = parseFloat(m.caVal) || 0, po = parseFloat(m.pmVal) || 0;
            const cr = m.kpi?.total_ca_ht || 0, pr = m.kpi?.avg_pm_ht || 0;
            const ec = co > 0 && cr > 0 ? cr - co : null, ep = po > 0 && pr > 0 ? pr - po : null;
            return (
              <tr key={k} className={m.isCurrentMonth ? 'kpi-obj-current' : ''}>
                <td className="kpi-obj-month">{MOIS_COURT[m.month]} {m.year}</td>
                <td>{ie ? <input className="form-input" type="number" value={editCa} onChange={e => setEditCa(e.target.value)} style={{ width: 90, fontSize: '0.78rem', padding: '4px 6px' }} /> : co > 0 ? <strong>{co.toLocaleString('fr-FR')} €</strong> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td>{cr > 0 ? `${cr.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €` : '—'}</td>
                <td>{ec !== null ? <span style={{ color: ec >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 600 }}>{ec >= 0 ? '+' : ''}{ec.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</span> : '—'}</td>
                <td>{ie ? <input className="form-input" type="number" step="0.01" value={editPm} onChange={e => setEditPm(e.target.value)} style={{ width: 80, fontSize: '0.78rem', padding: '4px 6px' }} /> : po > 0 ? <strong>{po.toLocaleString('fr-FR')} €</strong> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td>{pr > 0 ? `${pr.toFixed(2)} €` : '—'}</td>
                <td>{ep !== null ? <span style={{ color: ep >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 600 }}>{ep >= 0 ? '+' : ''}{ep.toFixed(2)} €</span> : '—'}</td>
                {canManageObjectives && <td>{ie ? <div style={{ display: 'flex', gap: 4 }}><button className="btn btn-primary btn-sm" style={{ fontSize: '0.65rem', padding: '2px 8px' }} onClick={() => saveObjEdit(m)}>✓</button><button className="btn btn-secondary btn-sm" style={{ fontSize: '0.65rem', padding: '2px 8px' }} onClick={() => setEditingObj(null)}>✕</button></div> : <button className="btn btn-secondary btn-sm" style={{ fontSize: '0.65rem', padding: '2px 8px' }} onClick={() => startEdit(m)}>✏️</button>}</td>}
              </tr>
            );
          })}</tbody></table>
        </div></div>
      </div>
    );
  }

  const modernStore = isModernKpiStore(currentStore);
  if (modernStore && modernExperience) {
    return <KpiModernTestPage
      store={currentStore}
      stores={stores}
      onStoreChange={handleStoreChange}
      onOpenStatistics={() => { setKpiView('stats'); setModernExperience(false); }}
      adminTools={isAdmin ? {
        importing,
        imports,
        importFile: handleImport,
        deleteMonth: handleDeleteMonth,
        openObjectives: () => setShowObjectives(true),
        reconciliation,
        closeReconciliation: () => setReconciliation(null),
      } : null}
    />;
  }

  // ========== MAIN RENDER ==========
  return (
    <div className="main-content">
      <div className="store-bar">{stores.map(s => <button key={s.id} className={`store-tab ${currentStore?.id === s.id ? 'active' : ''}`} onClick={() => handleStoreChange(s)}><span className="store-tab-dot" style={{ background: s.color }} />{s.name}</button>)}</div>

      <div className="kpi-topbar">
        <h2 className="kpi-page-title">📊 Chiffres</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', flex: 1 }}>
          {modernStore ? <div className="kpi-legacy-modern-nav"><button type="button" className="active">▥ Statistiques</button><button type="button" onClick={() => setModernExperience(true)}>▣ Vue Mois</button></div> : <><button className={`btn btn-sm ${kpiView === 'stats' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setKpiView('stats')}>📊 Statistiques</button><button className={`btn btn-sm ${kpiView === 'daily' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setKpiView('daily')} style={{ fontSize: '0.95rem', fontWeight: 700, padding: '6px 18px' }}>📅 JOUR / MOIS EN COURS</button><button className={`btn btn-sm ${kpiView === 'comparateur' ? 'btn-primary' : 'btn-secondary'}`} onClick={switchToComparateur}>📐 Comparateur</button></>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(canManageKpi || canManageObjectives) && (
            <div className="admin-menu" ref={menuRef}>
              <button className="btn btn-secondary btn-sm" onClick={() => setMenuOpen(!menuOpen)}>⚙️ Menu ▾</button>
              {menuOpen && (
                <div className="admin-menu-dropdown">
                  {canManageKpi && <button className="admin-menu-item" onClick={() => { setShowImport(true); setMenuOpen(false); }}>📥 Importer un mois</button>}
                  {canManageObjectives && <button className="admin-menu-item" onClick={() => { setShowObjectives(true); setMenuOpen(false); }}>🎯 Objectifs mensuels</button>}
                  {canManageKpi && <div className="admin-menu-divider" />}
                  {canManageKpi && <button className="admin-menu-item" onClick={() => { setShowImports(true); setMenuOpen(false); }}>📋 Mois importés</button>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }} className="loading-pulse">Chargement...</div>
      ) : (
        <>
          {/* ===== STATS VIEW ===== */}
          {kpiView === 'stats' && (
            <>
              <div className="kpi-charts-row">
                <SvgBarChart data={chartData} title="CA HT mensuel" valueKey="total_ca_ht" objKey="obj_ca_ht" unit="€" colorBar="#6088d4" colorCurrentMonth="#22c55e" decimals={0} />
                <SvgBarChart data={chartData} title="PM HT mensuel" valueKey="avg_pm_ht" objKey="obj_pm_ht" unit="€" colorBar="#8b5cf6" colorCurrentMonth="#f59e0b" decimals={2} />
              </div>

              {/* ===== SITUATION BLOCK ===== */}
              {situation && (
                <div className="kpi-situation-block" style={{ marginTop: 24 }}>
                  <div className="kpi-situation-header">
                    <div className="kpi-situation-title">
                      <span className="kpi-situation-icon">📊</span>
                      Situation du mois de {MOIS[situation.month]} {situation.year}
                    </div>
                    {situation.empty ? (
                      <div className="kpi-situation-date" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)', color: '#b45309' }}>Pas encore de données</div>
                    ) : (
                      <div className="kpi-situation-date">
                        Données jusqu'au <strong>{['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'][situation.lastDay.getDay()]} {situation.lastDay.getDate()} {MOIS[situation.lastDay.getMonth() + 1]}</strong>
                      </div>
                    )}
                  </div>

                  {situation.empty ? (
                    <div style={{ padding: '20px 24px' }}>
                      <div className="kpi-situation-msg" style={{ borderLeftColor: 'var(--accent-blue)', marginTop: 0 }}>
                        <span className="kpi-situation-msg-icon">🌅</span>
                        <span>C'est le début du mois, bon courage ! Saisissez vos premiers résultats dans l'onglet "Jour / Mois en cours".</span>
                      </div>
                      <div className="kpi-sit-forecast" style={{ margin: '20px 0 0' }}>
                        <div className="kpi-sit-forecast-header">
                          <span className="kpi-sit-forecast-icon">🎯</span>
                          <div className="kpi-sit-forecast-title">Prévisionnel</div>
                        </div>
                        <div className="kpi-sit-forecast-grid">
                          <div className="kpi-sit-forecast-item">
                            <div className="kpi-sit-forecast-label">Jours ouvrés ce mois</div>
                            <div className="kpi-sit-forecast-value">{situation.joursOuvres}<span>j</span></div>
                          </div>
                          {situation.n1Full && situation.n1Full.total_ca_ht > 0 && (
                            <div className="kpi-sit-forecast-item">
                              <div className="kpi-sit-forecast-label">CA N-1 sur ce mois</div>
                              <div className="kpi-sit-forecast-value">{situation.n1Full.total_ca_ht.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} <span>€</span></div>
                            </div>
                          )}
                          {situation.objCa > 0 && (
                            <div className="kpi-sit-forecast-item">
                              <div className="kpi-sit-forecast-label">Objectif du mois</div>
                              <div className="kpi-sit-forecast-value" style={{ color: '#f59e0b' }}>{situation.objCa.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} <span>€</span></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="kpi-situation-phrase">
                        {(() => {
                          const s = situation;
                          if (!s.n1 || s.n1.total_ca_ht === 0) return <div className="kpi-situation-msg" style={{ borderLeftColor: 'var(--text-muted)', marginTop: 0 }}><span className="kpi-situation-msg-icon">📋</span><span>Pas de données N-1 pour comparer. Continuez la saisie !</span></div>;
                          const n1 = s.n1;
                          const caR = n1.total_ca_ht > 0 ? s.current.ca / n1.total_ca_ht : 1;
                          const tkR = n1.total_tickets > 0 ? s.current.tickets / n1.total_tickets : 1;
                          const pmR = n1.avg_pm_ht > 0 ? s.current.pm / n1.avg_pm_ht : 1;
                          const caOk = caR >= 0.98, caG = caR >= 1.02, tkOk = tkR >= 0.97, tkG = tkR >= 1.02, pmOk = pmR >= 0.98, pmG = pmR >= 1.02;
                          let i, t, c;
                          if (caG && tkG && pmG) { i='🚀'; t='Excellente dynamique ! CA, tickets et PM sont tous au-dessus de N-1.'; c='var(--accent-green, #22c55e)'; }
                          else if (caG && tkG) { i='💪'; t='Le CA et les tickets sont au-dessus de N-1. Le PM pourrait être amélioré.'; c='var(--accent-green, #22c55e)'; }
                          else if (caG && !tkOk) { i='🎯'; t='Le CA est bon grâce au PM, mais le nombre de tickets est en retard. Travaillez la fréquentation.'; c='#f59e0b'; }
                          else if (caG) { i='✅'; t='Bonne performance ! Le CA est en avance sur N-1.'; c='var(--accent-green, #22c55e)'; }
                          else if (caOk && !tkOk) { i='⚠️'; t='Le CA est dans la cible mais les tickets sont en baisse. Attention à la fréquentation.'; c='#f59e0b'; }
                          else if (caOk && !pmOk) { i='📉'; t='Le CA est correct mais le PM est en retard. Travaillez la vente additionnelle.'; c='#f59e0b'; }
                          else if (caOk) { i='👍'; t='Le CA est dans les prévisions par rapport à N-1.'; c='var(--accent-blue)'; }
                          else if (!caOk && tkG) { i='⚡'; t='Le CA est en retard malgré un bon nombre de tickets. Le PM doit être amélioré.'; c='var(--accent-red)'; }
                          else if (!caOk && pmG) { i='📊'; t='Le PM est bon mais le CA est en retard par manque de tickets.'; c='var(--accent-red)'; }
                          else if (!caOk && !tkOk && !pmOk) { i='🔴'; t='Attention : CA, tickets et PM sont tous en retard par rapport à N-1.'; c='var(--accent-red)'; }
                          else { i='📉'; t='Le CA est en retard. Il reste du temps pour rattraper !'; c='var(--accent-red)'; }
                          return <div className="kpi-situation-msg" style={{ borderLeftColor: c, marginTop: 0 }}><span className="kpi-situation-msg-icon">{i}</span><span>{t}</span></div>;
                        })()}
                      </div>

                      <div className="kpi-sit-cards">
                        {(() => {
                          const fN = v => v ? v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : '0';
                          const fD = v => v ? v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';
                          const pct = (cv, r) => { if (!r || r === 0) return '—'; const p = ((cv - r) / r) * 100; return `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`; };
                          const cur = situation.current, ref = situation.n1;
                          const rows = [
                            { label: 'CA HT', cur: cur.ca, ref: ref.total_ca_ht, fmt: fN, unit: ' €', icon: '💰' },
                            { label: 'Tickets', cur: cur.tickets, ref: ref.total_tickets, fmt: fN, unit: '', icon: '🎫' },
                            { label: 'PM HT', cur: cur.pm, ref: ref.avg_pm_ht, fmt: fD, unit: ' €', icon: '🛒' },
                          ];
                          return (
                            <div className="kpi-sit-card">
                              <div className="kpi-sit-card-header"><span className="kpi-sit-card-icon">📅</span><div><div className="kpi-sit-card-title">vs N-1</div><div className="kpi-sit-card-sub">Année précédente · {ref.nb_jours || 0}j de données</div></div></div>
                              <div className="kpi-sit-rows">
                                {rows.map((row, ri) => { const diff = row.cur - row.ref; return (
                                  <div key={ri} className="kpi-sit-row">
                                    <div className="kpi-sit-row-label"><span>{row.icon}</span> {row.label}</div>
                                    <div className="kpi-sit-row-values"><span className="kpi-sit-current">{row.fmt(row.cur)}{row.unit}</span><span className="kpi-sit-vs">vs</span><span className="kpi-sit-ref">{row.fmt(row.ref)}{row.unit}</span></div>
                                    <div className={`kpi-sit-badge ${diff >= 0 ? 'up' : 'down'}`}>{diff >= 0 ? '▲' : '▼'} {pct(row.cur, row.ref)}</div>
                                  </div>
                                ); })}
                              </div>
                            </div>
                          );
                        })()}
                        {(() => {
                          const pct = (cv, r) => { if (!r || r === 0) return '—'; const p = ((cv - r) / r) * 100; return `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`; };
                          const cur = situation.current, ref = situation.m1;
                          const items = [{ label: 'CA HT', cur: cur.ca, ref: ref.total_ca_ht, icon: '💰' },{ label: 'Tickets', cur: cur.tickets, ref: ref.total_tickets, icon: '🎫' },{ label: 'PM HT', cur: cur.pm, ref: ref.avg_pm_ht, icon: '🛒' }];
                          return (
                            <div className="kpi-sit-card kpi-sit-card-compact">
                              <div className="kpi-sit-card-header"><span className="kpi-sit-card-icon">📆</span><div><div className="kpi-sit-card-title">vs M-1</div><div className="kpi-sit-card-sub">Mois précédent · {ref.nb_jours || 0}j</div></div></div>
                              <div className="kpi-sit-compact-rows">
                                {items.map((row, ri) => { const diff = row.cur - row.ref; return (
                                  <div key={ri} className="kpi-sit-compact-row"><span className="kpi-sit-compact-label">{row.icon} {row.label}</span><span className={`kpi-sit-badge ${diff >= 0 ? 'up' : 'down'}`}>{diff >= 0 ? '▲' : '▼'} {pct(row.cur, row.ref)}</span></div>
                                ); })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      <div className="kpi-sit-forecast">
                        <div className="kpi-sit-forecast-header"><span className="kpi-sit-forecast-icon">🎯</span><div className="kpi-sit-forecast-title">Prévisionnel</div></div>
                        <div className="kpi-sit-forecast-grid">
                          <div className="kpi-sit-forecast-item"><div className="kpi-sit-forecast-label">Jours restants sur le mois</div><div className="kpi-sit-forecast-value">{situation.joursRestants}<span>j</span> / {situation.joursOuvres}<span>j ouvrés</span></div></div>
                          {situation.n1Full && situation.n1Full.total_ca_ht > 0 && situation.joursRestants > 0 && (
                            <div className="kpi-sit-forecast-item"><div className="kpi-sit-forecast-label">Pour atteindre N-1</div>
                              <div className="kpi-sit-forecast-value" style={{ color: situation.current.ca >= situation.n1Full.total_ca_ht ? 'var(--accent-green, #22c55e)' : 'var(--accent-blue)' }}>{situation.current.ca >= situation.n1Full.total_ca_ht ? <span>✅ Déjà dépassé !</span> : <span>{Math.ceil((situation.n1Full.total_ca_ht - situation.current.ca) / situation.joursRestants).toLocaleString('fr-FR')} <span>€/j</span></span>}</div>
                              {situation.current.ca < situation.n1Full.total_ca_ht && <div className="kpi-sit-forecast-sub">N-1 : {situation.n1Full.total_ca_ht.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € · Reste : {Math.ceil(situation.n1Full.total_ca_ht - situation.current.ca).toLocaleString('fr-FR')} €</div>}
                            </div>
                          )}
                          {situation.objCa > 0 && situation.joursRestants > 0 && (
                            <div className="kpi-sit-forecast-item"><div className="kpi-sit-forecast-label">Pour atteindre l'objectif</div>
                              <div className="kpi-sit-forecast-value" style={{ color: situation.current.ca >= situation.objCa ? 'var(--accent-green, #22c55e)' : '#f59e0b' }}>{situation.current.ca >= situation.objCa ? <span>✅ Objectif atteint !</span> : <span>{Math.ceil((situation.objCa - situation.current.ca) / situation.joursRestants).toLocaleString('fr-FR')} <span>€/j</span></span>}</div>
                              {situation.current.ca < situation.objCa && <div className="kpi-sit-forecast-sub">Objectif : {situation.objCa.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € · Reste : {Math.ceil(situation.objCa - situation.current.ca).toLocaleString('fr-FR')} €</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ===== DAILY VIEW ===== */}
          {kpiView === 'daily' && (
            <div>
              <div className="kpi-chart-container">
                <h3 className="kpi-chart-title" style={{ fontSize: '1.15rem', fontWeight: 800 }}>📅 {MOIS[nowDate.getMonth() + 1]} {nowDate.getFullYear()} — JOUR / MOIS EN COURS</h3>
                <DailyChart dailyData={dailyInputs} />
              </div>

              {/* Month summary */}
              {dailyInputs.length > 0 && (
                <div className="kpi-daily-stats">
                  <div className="kpi-daily-stat-card">
                    <div className="kpi-daily-stat-label">CA HT cumulé</div>
                    <div className="kpi-daily-stat-value" style={{ color: 'var(--accent-blue)' }}>{monthTotalCa.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</div>
                    <div className="kpi-daily-stat-sub">{dailyInputs.length}j saisis</div>
                  </div>
                  <div className="kpi-daily-stat-card">
                    <div className="kpi-daily-stat-label">PM HT moyen</div>
                    <div className="kpi-daily-stat-value" style={{ color: '#f59e0b' }}>{monthAvgPm.toFixed(2)} €</div>
                    <div className="kpi-daily-stat-sub">moyenne / jour</div>
                  </div>
                  <div className="kpi-daily-stat-card">
                    <div className="kpi-daily-stat-label">Tickets moyen</div>
                    <div className="kpi-daily-stat-value" style={{ color: 'var(--accent-green)' }}>{monthAvgTickets.toFixed(1)}</div>
                    <div className="kpi-daily-stat-sub">total : {monthTotalTickets}</div>
                  </div>
                </div>
              )}

              {/* Input form */}
              {canEnterDailyResults && <div className="kpi-chart-container" style={{ maxWidth: 600, marginTop: 16 }}>
                <h3 className="kpi-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  ✏️ Saisir les résultats du jour
                  <span className="kpi-help-tooltip" data-tip="Modification : choisir le jour et remplir à nouveau les champs puis valider">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'block' }}>
                      <circle cx="8" cy="8" r="7" stroke="var(--text-muted)" strokeWidth="1.5" fill="none"/>
                      <text x="8" y="11.5" textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--text-muted)">?</text>
                    </svg>
                  </span>
                </h3>
                <div className="kpi-comp-row">
                  <div className="kpi-comp-field" style={{ flex: '1.5' }}>
                    <label>Date</label>
                    <input type="date" className="form-input" value={diDate} onChange={e => setDiDate(e.target.value)} style={{ minWidth: 150 }} />
                  </div>
                  <div className="kpi-comp-field" style={{ flex: '1.2' }}>
                    <label>CA HT (€)</label>
                    <input type="number" step="0.01" className="form-input" value={diCa} onChange={e => setDiCa(e.target.value)} placeholder="ex: 1200" />
                  </div>
                  <div className="kpi-comp-field" style={{ flex: '0.8' }}>
                    <label>PM HT (€)</label>
                    <input type="number" step="0.01" className="form-input" value={diPm} onChange={e => setDiPm(e.target.value)} placeholder="ex: 22.50" />
                  </div>
                  <div className="kpi-comp-field" style={{ flex: '0.7' }}>
                    <label>Nb tickets</label>
                    <input type="number" className="form-input" value={diTickets} onChange={e => setDiTickets(e.target.value)} placeholder="ex: 45" />
                  </div>
                </div>
                <button className="btn btn-primary" onClick={handleSaveDailyInput} style={{ marginTop: 10 }}>💾 Enregistrer</button>
              </div>}
            </div>
          )}

          {/* ===== COMPARATEUR VIEW ===== */}
          {kpiView === 'comparateur' && (
            <div className="kpi-chart-container" style={{ maxWidth: 800 }}>
              <h3 className="kpi-chart-title">📐 Comparateur mois en cours</h3>
              {!compResult ? (
                <div className="kpi-comp-form">
                  <div className="kpi-comp-row">
                    <div className="kpi-comp-field"><label>Date début</label><input type="date" className="form-input" value={compStart} onChange={e => setCompStart(e.target.value)} /></div>
                    <div className="kpi-comp-field"><label>Date fin</label><input type="date" className="form-input" value={compEnd} onChange={e => setCompEnd(e.target.value)} /></div>
                  </div>
                  <div className="kpi-comp-row">
                    <div className="kpi-comp-field"><label>CA HT (€)</label><input type="number" step="0.01" className="form-input" placeholder="ex: 15000" value={compCa} onChange={e => setCompCa(e.target.value)} /></div>
                    <div className="kpi-comp-field"><label>PM HT (€)</label><input type="number" step="0.01" className="form-input" placeholder="ex: 22.50" value={compPm} onChange={e => setCompPm(e.target.value)} /></div>
                    <div className="kpi-comp-field"><label>Nb tickets</label><input type="number" className="form-input" placeholder="ex: 350" value={compTickets} onChange={e => setCompTickets(e.target.value)} /></div>
                  </div>
                  <button className="btn btn-primary" onClick={handleCompare} disabled={compLoading} style={{ marginTop: 8 }}>
                    {compLoading ? 'Calcul...' : '📊 Comparer'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="kpi-comp-summary">
                    <div className="kpi-comp-summary-data">
                      <span>📅 {compResult.startLabel} → {compResult.endLabel}</span><span>·</span>
                      <span>CA: <strong>{compResult.current.ca.toLocaleString('fr-FR')} €</strong></span><span>·</span>
                      <span>PM: <strong>{compResult.current.pm.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></span><span>·</span>
                      <span>Tickets: <strong>{compResult.current.tickets}</strong></span>
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => setCompResult(null)}>✏️ Modifier</button>
                  </div>
                  <div className="kpi-comp-jours">📅 <strong>{compResult.joursOuvres}</strong> jours ouvrés · <strong>{compResult.joursRestants}</strong> jours restants</div>
                  <div className="kpi-comp-modules">
                    <CompCard title="📅 vs N-1 (année précédente)" subtitle={compResult.n1.period} nbJours={compResult.n1.nb_jours} current={compResult.current} ref_data={{ ca: compResult.n1.total_ca_ht, pm: compResult.n1.avg_pm_ht, tickets: compResult.n1.total_tickets }} joursRestants={compResult.joursRestants} isN1={true} />
                    <CompCard title="📅 vs M-1 (mois précédent)" subtitle={compResult.m1.period} nbJours={compResult.m1.nb_jours} current={compResult.current} ref_data={{ ca: compResult.m1.total_ca_ht, pm: compResult.m1.avg_pm_ht, tickets: compResult.m1.total_tickets }} joursRestants={0} isN1={false} />
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="modal-overlay" onClick={() => setShowImport(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header"><h2>📥 Importer un mois</h2><button className="modal-close" onClick={() => setShowImport(false)}>&times;</button></div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 16, padding: '14px 16px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: '1.6rem' }}>📊</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Fichier Excel requis (.xlsx)</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Format attendu : fichier Excel Microsoft (.xlsx ou .xls)</div>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}><strong style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Magasin : {currentStore?.name}</strong></div>
              <label className="kpi-file-input" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', border: '2px dashed var(--border-color)', borderRadius: 'var(--radius-sm)', cursor: importing ? 'not-allowed' : 'pointer', transition: 'border-color 0.15s', background: 'var(--bg-surface)' }}>
                <span style={{ fontSize: '1.2rem' }}>📎</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--accent-blue)' }}>Parcourir...</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{fileRef.current?.files?.[0]?.name || 'Aucun fichier sélectionné'}</div>
                </div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} disabled={importing} style={{ display: 'none' }} />
              </label>
              {importing && <div style={{ marginTop: 12, textAlign: 'center', color: 'var(--accent-blue)' }} className="loading-pulse">Import en cours...</div>}
            </div>
          </div>
        </div>
      )}

      {/* Imports list */}
      {showImports && (
        <div className="modal-overlay" onClick={() => setShowImports(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header"><h2>📋 Mois importés</h2><button className="modal-close" onClick={() => setShowImports(false)}>&times;</button></div>
            <div style={{ padding: 20 }}>
              {imports.length === 0 ? <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Aucun import</p> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {imports.map((imp, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-light)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{MOIS[imp.month]} {imp.year}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{imp.nb_jours_ouvres}j · CA HT: {imp.total_ca_ht.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</div>
                      </div>
                      {canManageKpi && <button className="btn btn-danger btn-sm" style={{ fontSize: '0.7rem' }} onClick={() => handleDeleteMonth(imp.month, imp.year)}>🗑️</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reconciliation popup after import */}
      {reconciliation && (
        <div className="modal-overlay" onClick={() => setReconciliation(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>📊 Réconciliation — {MOIS[reconciliation.month]} {reconciliation.year}</h2>
              <button className="modal-close" onClick={() => setReconciliation(null)}>&times;</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                Des saisies manuelles existaient pour ce mois. Voici la comparaison avec les données importées. <strong>Les données importées sont désormais utilisées.</strong>
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--text-muted)' }}></th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Saisie manuelle</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Import fichier</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'CA HT', inputVal: reconciliation.input.ca_ht, importVal: reconciliation.imported.ca_ht, unit: ' €', dec: 2 },
                    { label: 'PM HT', inputVal: reconciliation.input.pm_ht, importVal: reconciliation.imported.pm_ht, unit: ' €', dec: 2 },
                    { label: 'Tickets', inputVal: reconciliation.input.tickets, importVal: reconciliation.imported.tickets, unit: '', dec: 0 },
                    { label: 'Jours', inputVal: reconciliation.input.days, importVal: reconciliation.imported.days, unit: '', dec: 0 },
                  ].map((row, i) => {
                    const diff = row.importVal - row.inputVal;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{row.label}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.inputVal.toLocaleString('fr-FR', { minimumFractionDigits: row.dec, maximumFractionDigits: row.dec })}{row.unit}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{row.importVal.toLocaleString('fr-FR', { minimumFractionDigits: row.dec, maximumFractionDigits: row.dec })}{row.unit}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: diff === 0 ? 'var(--text-muted)' : diff > 0 ? 'var(--accent-green, #22c55e)' : 'var(--accent-red)' }}>
                          {diff === 0 ? '=' : `${diff > 0 ? '+' : ''}${diff.toLocaleString('fr-FR', { minimumFractionDigits: row.dec, maximumFractionDigits: row.dec })}${row.unit}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <button className="btn btn-primary" onClick={() => setReconciliation(null)}>Compris</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
